import { useEffect, useMemo, useRef, useState } from "react";
import useModalActions from "@hooks/common/useModalAction";
import TaviReportModalContent from "./TaviReportModalContent";
import BaseModalContent from "../BaseReportModalContent/BaseReportModalContetnt";
import { useReportModal } from "@hooks/useReportModal";

import {
  ContextKey,
  SurfaceContext,
  VRContext,
  useBufferPool,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';

import {
  captureSurface3D,
  cropToSquare,
} from 'utils/captureUtils';
import { useAnalysisPage } from "@features/analysis/useAnalysisPage";
import { useBrowserPage } from "@features/browser/useBrowserPage";
import { TaviReportSection } from "@components/ReportTable/TaviReportTable";
import { getTaviParsedData } from "@features/analysis/rendering/Panels/Renderers/taviDataUtils";
import { usePanelContainer } from "@features/analysis/rendering/Panels/usePanelContainer";

const TaviReportModal = ({
  onClose,
}:{
  onClose: ()=>void,
}) => {

  const { analysisType } = usePanelContainer();

  const { selectedSeriesId } = useBrowserPage();
  const { contextPool, bufferPool } = useBufferPool();

  const { selectedLayoutConfig } = useAnalysisPage();
  const { waitingAction, closeLoadingModal } = useModalActions();

  const poolRef = useRef(contextPool);
  const resolvedSeriesId = selectedSeriesId ?? Object.keys(bufferPool)[0];
  const pool = resolvedSeriesId ? bufferPool[resolvedSeriesId] : undefined;
  const [imageMap, setImageMap] = useState<{ one?: string }>({});
  const hasVR = selectedLayoutConfig.panelConfig.some(
    (panel) => panel.componentKey === 'VR',
  );
  const parsed = getTaviParsedData(pool);
  const taviSections = useMemo<TaviReportSection[]>(() => {
    const avg = (a: number | null, b: number | null) =>
      a !== null && b !== null ? (a + b) / 2 : null;

    const annulus = parsed?.annulus ?? null;
    const stj = parsed?.sinotubularjunction ?? null;
    const ostium = parsed?.ostium
      ? { lca: parsed.ostium.lca.length, rca: parsed.ostium.rca.length }
      : null;
    const measurements = parsed?.measurements ?? null;

    const lcc = measurements?.LCC_len ?? null;
    const rcc = measurements?.RCC_len ?? null;
    const ncc = measurements?.NCC_len ?? null;
    const avgValsalva =
      lcc !== null && rcc !== null && ncc !== null
        ? (lcc + rcc + ncc) / 3
        : null;

    return [
      {
        title: 'Annulus',
        rows: [
          {
            label: 'Short Axis',
            value: annulus?.minDiameter ?? null,
            suffix: 'mm',
          },
          {
            label: 'Long Axis',
            value: annulus?.maxDiameter ?? null,
            suffix: 'mm',
          },
          { label: 'Area', value: annulus?.areaSize ?? null, suffix: ' mm3/g' },
          {
            label: 'Perimeter',
            value: annulus?.areaPerimeter ?? null,
            suffix: 'mm',
          },
          {
            label: '(Long Axis + Short Axis) / 2',
            value: avg(
              annulus?.maxDiameter ?? null,
              annulus?.minDiameter ?? null,
            ),
            suffix: 'mm',
          },
        ],
      },
      {
        title: 'Sinotubular Junction',
        rows: [
          {
            label: 'Short Axis',
            value: stj?.minDiameter ?? null,
            suffix: 'mm',
          },
          { label: 'Long Axis', value: stj?.maxDiameter ?? null, suffix: 'mm' },
          {
            label: '(Long Axis + Short Axis) / 2',
            value: avg(stj?.maxDiameter ?? null, stj?.minDiameter ?? null),
            suffix: 'mm',
          },
        ],
      },
      {
        title: 'Sinus of Valsalva',
        rows: [
          { label: 'Left coronary cusp diameter', value: lcc, suffix: 'mm' },
          { label: 'Right coronary cusp diameter', value: rcc, suffix: 'mm' },
          { label: 'Non-coronary cusp diameter', value: ncc, suffix: 'mm' },
          { label: 'Average diameter', value: avgValsalva, suffix: 'mm' },
        ],
      },
      {
        title: 'Distances',
        rows: [
          { label: 'LM height', value: ostium?.lca ?? null, suffix: 'mm' },
          { label: 'RCA height', value: ostium?.rca ?? null, suffix: 'mm' },
          {
            label: 'STJ height',
            value: measurements?.STJ_height ?? null,
            suffix: 'mm',
          },
        ],
      },
    ];
  }, [parsed]);


  useEffect(() => {
    poolRef.current = contextPool;
  }, [contextPool]);

  async function capture3d(
  ): Promise<string | undefined> {

    const contextKey: ContextKey = hasVR ? `VR` : `Surface3D`;
    const d3context = {
      current: poolRef.current[contextKey] as VRContext | SurfaceContext,
    };
    if (!d3context) return;
    if (!d3context.current) return;

    waitingAction({ message: 'Waiting' });
    let url = await captureSurface3D(d3context.current, analysisType, pool);
    url = await cropToSquare(url!);
    setImageMap({one: url,});
    closeLoadingModal();
  }

  useEffect(() => {
    capture3d();
  }, []);

  // const setter = async (args: any)=>{
  //   (args as [string,Promise<any>][]).forEach(async (value)=>{
  //     const img =  await value[1];
  //     setImageMap( (prev)=>({
  //       ...prev,
  //       [value[0]] :img
  //     }))
  //   })
  // }


  // useEffect(() => {
  //   waitingAction({ message: 'Waiting' });
  // }, []);

  // useEffect(()=>{
  //   if(isDone){
  //     closeLoadingModal();
  //   }else if(!Object.keys(imageMap).length){
  //     console.log('모여?')
  //    // scheduleRender('Surface3D', { type: 'captureMyocardial',setter});
  //   }
  // },[isDone])

 // if(!isDone)return;


  return (
    <BaseModalContent
      title="Tavicardial Mass at Risk Analysis Report"
      // vw={68}
      // vh={80}
      onClose={onClose}
    >
      <TaviReportModalContent
        imageSrc={{ images: imageMap }}
        value={taviSections}
      />
    </BaseModalContent>
  );
}

export function useTaviReportModal(res:{updateActivatedTool: (tool: any,check: boolean) => void}){
  return useReportModal({Component:TaviReportModal,...res});
}
